<template>
  <div class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" @click="cancelModal"></div>
    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div
          class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 rounded-xl shadow-2xl text-white overflow-hidden flex flex-col"
        >
          <!-- Header -->
          <div class="flex justify-between items-center p-6 border-b border-gray-700">
            <h2 id="modal-title" class="text-xl font-semibold text-indigo-300">สร้างชั้นใต้ดิน</h2>
            <button
              @click="cancelModal"
              class="text-gray-400 hover:text-white focus:outline-none"
              aria-label="ปิด"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Tab Navigation -->
          <div class="flex border-b border-gray-700 bg-gray-800">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="switchTab(tab.id)"
              class="flex-1 px-4 py-3 text-sm font-medium text-white transition duration-200"
              :class="{
                'bg-indigo-600 border-b-2 border-indigo-500': currentTab === tab.id,
                'bg-gray-600 hover:bg-gray-500': currentTab !== tab.id
              }"
            >
              {{ tab.name }}
            </button>
          </div>

          <!-- Tab Content -->
          <div
            class="h-96 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700"
          >
            <form @submit.prevent="handleAddBasement">
              <!-- Basic Info Tab -->
              <div v-if="currentTab === 'basic'" class="space-y-4">
                <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">
                  ข้อมูลพื้นฐาน
                </h3>
                <div class="grid grid-cols-1 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >ชื่อชั้น (เช่น b1, b2) <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model="name"
                      type="text"
                      placeholder="เช่น b1"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >ลิงก์รูปภาพ <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model="imageUrl"
                      type="url"
                      placeholder="https://example.com/basement.jpg"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                    <div v-if="imageUrl" class="mt-2">
                      <img
                        :src="imageUrl"
                        alt="Basement Preview"
                        class="w-24 h-24 object-cover rounded-lg"
                        @error="imageUrl = ''"
                      />
                    </div>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >เลเวลที่ต้องการ <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="requiredLevel"
                      type="number"
                      min="1"
                      placeholder="1"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >จำนวนห้องที่ต้องการ <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="roomRequired"
                      type="number"
                      min="1"
                      placeholder="1"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >พลังงานที่ต้องการ <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="requiredEnergy"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >ลดความสุข <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="reduceHappy"
                      type="number"
                      min="0"
                      placeholder="1"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1">อนุญาตให้ค้นหาผู้เล่นคนอื่น</label>
                    <input
                      v-model="allowSearchOtherPlayers"
                      type="checkbox"
                      class="w-5 h-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <h4 class="text-md font-medium text-gray-300 mt-4">ค่าเข้า</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >เหรียญ <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="requiredCoins"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >ทอง <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="requiredGold"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >เพชร <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="requiredDiamonds"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >แคช <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="requiredCash"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <h4 class="text-md font-medium text-gray-300 mt-4">การสูญเสีย</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนไอเทมในกระเป๋าที่เสียขั้นต่ำ <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="minBagItemsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนไอเทมในกระเป๋าที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="maxBagItemsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนทักษะที่เสียขั้นต่ำ <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="minSkillsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนทักษะที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="maxSkillsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนรอยสักที่เสียขั้นต่ำ <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="minTattoosToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนรอยสักที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="maxTattoosToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนไอเทมที่ใส่อยู่ที่เสียขั้นต่ำ <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="minEquippedItemsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนไอเทมที่ใส่อยู่ที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="maxEquippedItemsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนห้องที่เสียขั้นต่ำ <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="minRoomsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >จำนวนห้องที่เสียขั้นสูงสุด <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="maxRoomsToLose"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <h4 class="text-md font-medium text-gray-300 mt-4">คูลดาวน์</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >คูลดาวน์การค้นหา (วินาที) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="searchCooldown"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >คูลดาวน์การค้นหาผู้เล่นอื่น (วินาที) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="searchOtherPlayersCooldown"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >คูลดาวน์การประมวลผลการต่อสู้ (วินาที) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="combatProcessingCooldown"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >คูลดาวน์การออกจากชั้นใต้ดิน (วินาที) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="exitBasementCooldown"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <h4 class="text-md font-medium text-gray-300 mt-4">โอกาสการพบ</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >โอกาสพบอาชญากร (%) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="criminalEncounterChance"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >โอกาสพบผู้เล่นอื่น (%) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="otherPlayerEncounterChance"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <h4 class="text-md font-medium text-gray-300 mt-4">พลังงานและระยะเวลา</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >ระยะเวลาหมดอายุของอีเวนต์ (วินาที) <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="eventExpirationTime"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >พลังงานที่ใช้สำหรับการค้นหา <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="searchEnergyCost"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1"
                        >พลังงานที่ใช้สำหรับค้นหาผู้เล่นอื่น <span class="text-red-500">*</span></label
                      >
                      <input
                        v-model.number="searchOtherPlayersEnergyCost"
                        type="number"
                        min="0"
                        placeholder="0"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Criminals Tab -->
              <div v-if="currentTab === 'criminals'" class="space-y-4">
                <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">
                  อาชญากรในชั้นใต้ดิน
                </h3>
                <div class="space-y-3">
                  <div
                    v-for="(criminal, index) in criminals"
                    :key="index"
                    class="grid grid-cols-12 items-center gap-3 animate-fade-in"
                  >
                    <div class="col-span-6 sm:col-span-7">
                      <input
                        v-model="criminal.name"
                        type="text"
                        placeholder="ชื่ออาชญากร (เช่น Thief)"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      />
                    </div>
                    <div class="col-span-4 sm:col-span-3">
                      <input
                        v-model.number="criminal.chance"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="%"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      />
                    </div>
                    <div class="col-span-2">
                      <button
                        v-if="index > 0"
                        @click="removeCriminal(index)"
                        type="button"
                        class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                        aria-label="ลบอาชญากร"
                      >
                        ลบ
                      </button>
                    </div>
                  </div>
                  <div class="flex gap-3 mt-3">
                    <button
                      @click="addCriminal"
                      type="button"
                      class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                    >
                      + เพิ่มอาชญากร
                    </button>
                    <button
                      @click="normalizeChances"
                      type="button"
                      class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                    >
                      ปรับโอกาสให้เท่ากัน
                    </button>
                  </div>
                  <div v-if="totalChance > 0" class="text-sm text-gray-400 mt-2">
                    <p>โอกาสการเจออาชญากร (รวม: {{ totalChance.toFixed(2) }}%):</p>
                    <p
                      v-if="totalChance < 100"
                      class="text-yellow-500"
                    >
                      ขาด: {{ (100 - totalChance).toFixed(2) }}%
                    </p>
                    <p
                      v-else-if="totalChance > 100"
                      class="text-red-500"
                    >
                      เกิน: {{ (totalChance - 100).toFixed(2) }}%
                    </p>
                    <p
                      v-else
                      class="text-green-500"
                    >
                      ครบ 100%
                    </p>
                    <ul class="list-disc list-inside">
                      <li
                        v-for="(criminal, index) in criminals"
                        :key="index"
                      >
                        {{ criminal.name || 'ยังไม่ระบุ' }}: {{ criminal.chance || 0 }}%
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Rewards Tab -->
              <div v-if="currentTab === 'rewards'" class="space-y-4">
                <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">
                  รางวัลเมื่อสำเร็จ
                </h3>
                <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลเงินตรา</h4>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >เหรียญเงิน (Silver) <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="silver"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >ทอง (Gold) <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="gold"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1"
                      >เพชร (Diamond) <span class="text-red-500">*</span></label
                    >
                    <input
                      v-model.number="diamond"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      required
                      aria-required="true"
                    />
                  </div>
                </div>
                <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลไอเทม</h4>
                <div class="space-y-3">
                  <div
                    v-for="(reward, index) in rewards"
                    :key="index"
                    class="grid grid-cols-12 items-center gap-3 animate-fade-in"
                  >
                    <div class="col-span-6 sm:col-span-7">
                      <input
                        v-model="reward.itemName"
                        type="text"
                        placeholder="ชื่ออุปกรณ์ (เช่น กระสุน)"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      />
                    </div>
                    <div class="col-span-4 sm:col-span-3">
                      <input
                        v-model.number="reward.dropChance"
                        type="number"
                        min="0"
                        max="10000"
                        placeholder="จาก 10,000"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                      />
                    </div>
                    <div class="col-span-2">
                      <button
                        @click="removeReward(index)"
                        type="button"
                        class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                        aria-label="ลบรางวัล"
                      >
                        ลบ
                      </button>
                    </div>
                  </div>
                  <button
                    @click="addReward"
                    type="button"
                    class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                  >
                    + เพิ่มรางวัล
                  </button>
                </div>
              </div>
            </form>
          </div>

          <!-- Fixed Action Buttons -->
          <div class="flex justify-end gap-3 p-6 border-t border-gray-700 bg-gray-800">
            <button
              @click="cancelModal"
              type="button"
              class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-200"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              @click="handleAddBasement"
              class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
              :disabled="totalChance !== 100"
            >
              สร้างชั้นใต้ดิน
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import { ref, computed } from 'vue';

export default {
  setup(props, context) {
    const cancelModal = () => {
      context.emit('cancelModal');
    };

    const name = ref('');
    const imageUrl = ref('');
    const requiredLevel = ref(1);
    const roomRequired = ref(1);
    const requiredEnergy = ref(0);
    const reduceHappy = ref(1);
    const requiredCoins = ref(0);
    const requiredGold = ref(0);
    const requiredDiamonds = ref(0);
    const requiredCash = ref(0);
    const criminals = ref([{ name: '', chance: 0 }]);
    const rewards = ref([{ itemName: '', dropChance: 0 }]);
    const itemsToLoseCount = ref(0);
    const skillsToLoseCount = ref(0);
    const tattoosToLoseCount = ref(0);
    const bagItemsToLoseCount = ref(0);
    const silver = ref(0);
    const gold = ref(0);
    const diamond = ref(0);
    const allowSearchOtherPlayers = ref(false);
    const minBagItemsToLose = ref(0);
    const maxBagItemsToLose = ref(0);
    const minSkillsToLose = ref(0);
    const maxSkillsToLose = ref(0);
    const minTattoosToLose = ref(0);
    const maxTattoosToLose = ref(0);
    const minEquippedItemsToLose = ref(0);
    const maxEquippedItemsToLose = ref(0);
    const minRoomsToLose = ref(0);
    const maxRoomsToLose = ref(0);
    const searchCooldown = ref(0);
    const searchOtherPlayersCooldown = ref(0);
    const combatProcessingCooldown = ref(0);
    const exitBasementCooldown = ref(0);
    const criminalEncounterChance = ref(0);
    const otherPlayerEncounterChance = ref(0);
    const eventExpirationTime = ref(0);
    const searchEnergyCost = ref(0);
    const searchOtherPlayersEnergyCost = ref(0);

    // Tab Management
    const tabs = [
      { id: 'basic', name: 'ข้อมูลพื้นฐาน' },
      { id: 'criminals', name: 'อาชญากรในชั้นใต้ดิน' },
      { id: 'rewards', name: 'รางวัล' }
    ];
    const currentTab = ref('basic');

    const switchTab = (tabId) => {
      currentTab.value = tabId;
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const totalChance = computed(() => {
      return criminals.value.reduce((sum, criminal) => sum + (criminal.chance || 0), 0);
    });

    const normalizeChances = () => {
      const numCriminals = criminals.value.length;
      const equalChance = Math.floor(100 / numCriminals);
      let remaining = 100 - equalChance * numCriminals;

      criminals.value.forEach((criminal, index) => {
        criminal.chance = equalChance + (index < remaining ? 1 : 0);
      });
    };

    const addCriminal = () => {
      criminals.value.push({ name: '', chance: 0 });
      normalizeChances();
    };

    const removeCriminal = (index) => {
      if (criminals.value.length > 1) {
        criminals.value.splice(index, 1);
        normalizeChances();
      }
    };

    const addReward = () => {
      rewards.value.push({ itemName: '', dropChance: 0 });
    };

    const removeReward = (index) => {
      rewards.value.splice(index, 1);
    };

    const handleAddBasement = async () => {
      if (totalChance.value !== 100) {
        alert('โอกาสการเจออาชญากรรวมกันต้องเท่ากับ 100%');
        return;
      }

      try {
        const filteredRewards = rewards.value.filter(r => r.itemName && r.dropChance > 0);
        const filteredCriminals = criminals.value.filter(c => c.name && c.chance > 0);
        const basementData = {
          name: name.value,
          imageUrl: imageUrl.value,
          requiredLevel: requiredLevel.value,
          roomRequired: roomRequired.value,
          requiredEnergy: requiredEnergy.value,
          reduceHappy: reduceHappy.value,
          requiredCoins: requiredCoins.value,
          requiredGold: requiredGold.value,
          requiredDiamonds: requiredDiamonds.value,
          requiredCash: requiredCash.value,
          criminals: filteredCriminals.map(c => ({ name: c.name, chance: c.chance || 0 })),
          criminalNames: filteredCriminals.map(c => c.name),
          rewards: filteredRewards,
          rewardItemNames: filteredRewards.map(r => r.itemName),
          itemsToLoseCount: itemsToLoseCount.value,
          skillsToLoseCount: skillsToLoseCount.value,
          tattoosToLoseCount: tattoosToLoseCount.value,
          bagItemsToLoseCount: bagItemsToLoseCount.value,
          rewardSilver: silver.value,
          rewardGold: gold.value,
          rewardDiamond: diamond.value,
          allowSearchOtherPlayers: allowSearchOtherPlayers.value,
          minBagItemsToLose: minBagItemsToLose.value,
          maxBagItemsToLose: maxBagItemsToLose.value,
          minSkillsToLose: minSkillsToLose.value,
          maxSkillsToLose: maxSkillsToLose.value,
          minTattoosToLose: minTattoosToLose.value,
          maxTattoosToLose: maxTattoosToLose.value,
          minEquippedItemsToLose: minEquippedItemsToLose.value,
          maxEquippedItemsToLose: maxEquippedItemsToLose.value,
          minRoomsToLose: minRoomsToLose.value,
          maxRoomsToLose: maxRoomsToLose.value,
          searchCooldown: searchCooldown.value,
          searchOtherPlayersCooldown: searchOtherPlayersCooldown.value,
          combatProcessingCooldown: combatProcessingCooldown.value,
          exitBasementCooldown: exitBasementCooldown.value,
          criminalEncounterChance: criminalEncounterChance.value,
          otherPlayerEncounterChance: otherPlayerEncounterChance.value,
          eventExpirationTime: eventExpirationTime.value,
          searchEnergyCost: searchEnergyCost.value,
          searchOtherPlayersEnergyCost: searchOtherPlayersEnergyCost.value,
          createdAt: new Date(),
        };

        const docRef = await projectFirestore.collection('basements').doc(name.value).get();
        if (docRef.exists) {
          alert('ชื่อชั้นนี้มีอยู่แล้ว กรุณาใช้ชื่ออื่น');
          return;
        }

        await projectFirestore.collection('basements').doc(name.value).set(basementData);
        context.emit('basementAdded');

        // Reset form
        name.value = '';
        imageUrl.value = '';
        requiredLevel.value = 1;
        roomRequired.value = 1;
        requiredEnergy.value = 0;
        reduceHappy.value = 1;
        requiredCoins.value = 0;
        requiredGold.value = 0;
        requiredDiamonds.value = 0;
        requiredCash.value = 0;
        criminals.value = [{ name: '', chance: 0 }];
        rewards.value = [{ itemName: '', dropChance: 0 }];
        itemsToLoseCount.value = 0;
        skillsToLoseCount.value = 0;
        tattoosToLoseCount.value = 0;
        bagItemsToLoseCount.value = 0;
        silver.value = 0;
        gold.value = 0;
        diamond.value = 0;
        allowSearchOtherPlayers.value = false;
        minBagItemsToLose.value = 0;
        maxBagItemsToLose.value = 0;
        minSkillsToLose.value = 0;
        maxSkillsToLose.value = 0;
        minTattoosToLose.value = 0;
        maxTattoosToLose.value = 0;
        minEquippedItemsToLose.value = 0;
        maxEquippedItemsToLose.value = 0;
        minRoomsToLose.value = 0;
        maxRoomsToLose.value = 0;
        searchCooldown.value = 0;
        searchOtherPlayersCooldown.value = 0;
        combatProcessingCooldown.value = 0;
        exitBasementCooldown.value = 0;
        criminalEncounterChance.value = 0;
        otherPlayerEncounterChance.value = 0;
        eventExpirationTime.value = 0;
        searchEnergyCost.value = 0;
        searchOtherPlayersEnergyCost.value = 0;
      } catch (error) {
        console.error('Error adding basement:', error);
        alert('เกิดข้อผิดพลาดในการสร้างชั้นใต้ดิน');
      }
    };

    return {
      cancelModal,
      name,
      imageUrl,
      requiredLevel,
      roomRequired,
      requiredEnergy,
      reduceHappy,
      requiredCoins,
      requiredGold,
      requiredDiamonds,
      requiredCash,
      criminals,
      rewards,
      itemsToLoseCount,
      skillsToLoseCount,
      tattoosToLoseCount,
      bagItemsToLoseCount,
      silver,
      gold,
      diamond,
      allowSearchOtherPlayers,
      minBagItemsToLose,
      maxBagItemsToLose,
      minSkillsToLose,
      maxSkillsToLose,
      minTattoosToLose,
      maxTattoosToLose,
      minEquippedItemsToLose,
      maxEquippedItemsToLose,
      minRoomsToLose,
      maxRoomsToLose,
      searchCooldown,
      searchOtherPlayersCooldown,
      combatProcessingCooldown,
      exitBasementCooldown,
      criminalEncounterChance,
      otherPlayerEncounterChance,
      eventExpirationTime,
      searchEnergyCost,
      searchOtherPlayersEnergyCost,
      totalChance,
      normalizeChances,
      addCriminal,
      removeCriminal,
      addReward,
      removeReward,
      handleAddBasement,
      tabs,
      currentTab,
      switchTab
    };
  },
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scroll-smooth {
  scroll-behavior: smooth;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #6366f1 #4b5563;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #4b5563;
  border-radius: 4px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 4px;
  border: 2px solid #4b5563;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #818cf8;
}
</style>